import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../components/ConfirmDialog";

export default function ChatThread() {
  const { otherUserId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [warning, setWarning] = useState("");
  const scrollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/messages/with/${otherUserId}`);
    setMessages(data.messages || []);
    setOther(data.other_user || null);
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [otherUserId]);
  useEffect(() => {
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [otherUserId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    setWarning("");
    try {
      const { data } = await api.post("/messages", { to_user_id: otherUserId, text: text.trim() });
      setMessages((m) => [...m, data]);
      setText("");
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (err) {
      setWarning(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar el mensaje");
    } finally { setSending(false); }
  };

  const initials = (other?.name || "?").substring(0, 1).toUpperCase();

  return (
    <div className="flex flex-col h-[calc(100vh-180px)] md:h-[calc(100vh-120px)] max-w-3xl mx-auto kibli-card overflow-hidden">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
        <Link to="/messages" className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100" data-testid="chat-back-btn">
          <i className="fa-solid fa-arrow-left"></i>
        </Link>
        <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">{initials}</div>
        <div className="flex-1 min-w-0">
          <div className="font-bold truncate" data-testid="chat-other-name">{other?.name || "Cargando…"}</div>
          <div className="text-xs text-slate-500 truncate">
            {other?.role === "profesional" ? "Profesional" : other?.role === "admin" ? "Administrador" : "Usuario"}
          </div>
        </div>
        <button
          onClick={async () => {
            const ok = await confirm({
              title: "¿Eliminar toda la conversación?",
              message: "Esta acción no se puede deshacer.",
              confirmLabel: "Eliminar",
            });
            if (!ok) return;
            try {
              await api.delete(`/messages/thread/${otherUserId}`);
              navigate("/messages");
            } catch (err) {
              setWarning(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar");
            }
          }}
          data-testid="chat-delete-thread-btn"
          className="w-10 h-10 flex items-center justify-center rounded-full text-slate-500 hover:bg-red-50 hover:text-red-600"
          title="Eliminar conversación"
        >
          <i className="fa-solid fa-trash"></i>
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
        {messages.length === 0 && (
          <div className="text-center text-slate-400 text-sm py-10" data-testid="chat-empty">Inicia la conversación con un saludo 👋</div>
        )}
        {messages.map((m) => {
          const mine = m.from_user_id === user?.id;
          return (
            <div key={m.id || m.created_at} className={`group flex items-center gap-2 ${mine ? "justify-end" : "justify-start"}`}>
              {mine && (
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await confirm({ title: "¿Eliminar este mensaje?", confirmLabel: "Eliminar" });
                    if (!ok) return;
                    try {
                      await api.delete(`/messages/${m.id}`);
                      setMessages((arr) => arr.filter((x) => x.id !== m.id));
                    } catch (err) {
                      setWarning(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar");
                    }
                  }}
                  data-testid={`delete-msg-${m.id}`}
                  className="opacity-60 md:opacity-0 md:group-hover:opacity-100 transition-opacity w-7 h-7 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center"
                  title="Eliminar mensaje"
                >
                  <i className="fa-solid fa-trash text-xs"></i>
                </button>
              )}
              <div className={`max-w-[75%] px-4 py-2 rounded-2xl ${mine ? "bg-blue-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                {m.text}
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="border-t border-slate-100 bg-white">
        {warning && (
          <div className="mx-3 mt-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-300 text-amber-800 text-sm flex items-start gap-2" data-testid="chat-warning">
            <i className="fa-solid fa-shield-halved mt-0.5"></i>
            <div className="flex-1">{warning}</div>
            <button type="button" onClick={() => setWarning("")} className="text-amber-700 hover:text-amber-900">
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
        )}
        <div className="flex gap-2 p-3">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Escribe un mensaje…"
            data-testid="chat-input"
            className="input-base flex-1"
          />
          <button type="submit" disabled={sending || !text.trim()} data-testid="chat-send-btn" className="btn-primary px-5">
            <i className="fa-solid fa-paper-plane"></i>
          </button>
        </div>
        <p className="px-4 pb-3 text-xs text-slate-400 flex items-center gap-1">
          <i className="fa-solid fa-lock"></i>
          Por tu seguridad, no se permiten emails, teléfonos ni enlaces externos.
        </p>
      </form>
    </div>
  );
}
